<template>
  <b-modal
    id="edit-user-modal"
    centered
    size="lg"
    :no-close-on-esc="true"
    :no-close-on-backdrop="true"
    dialog-class="my-dialog-class"
  >
    <template #modal-title> <h2 class="m-0">Edit User</h2> </template>
    <validation-observer ref="editUserFormValidation">
      <b-form @submit.prevent>
        <b-form-group label-for="username">
          <template #label>
            Username <span class="text-danger">(You can't change it)</span>
          </template>
          <validation-provider
            #default="{ errors }"
            name="Username"
          >
            <b-form-input
              id="username"
              v-model="username"
              :state="errors.length > 0 ? false : null"
              placeholder="Username"
              disabled
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group label-for="password">
          <template #label>
            Password
          </template>
          <validation-provider
            #default="{ errors }"
            name="Password"
          >
            <b-input-group
              class="input-group-merge"
              :class="errors.length > 0 ? 'is-invalid' : null"
            >
              <b-form-input
                id="password"
                v-model="password"
                :state="errors.length > 0 ? false : null"
                class="form-control-merge"
                :type="passwordFieldType"
                name="password"
                placeholder="············"
              />
              <b-input-group-append is-text>
                <feather-icon
                  class="cursor-pointer"
                  :icon="passwordToggleIcon"
                  @click="togglePasswordVisibility"
                />
              </b-input-group-append>
            </b-input-group>
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group label-for="Name">
          <template #label>
            Name <span class="text-danger">*</span>
          </template>
          <validation-provider
            #default="{ errors }"
            name="Name"
          >
            <b-form-input
              id="name"
              v-model="name"
              :state="errors.length > 0 ? false : null"
              placeholder="Name"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group label-for="mobile_number">
          <template #label>
            Mobile <span class="text-danger">*</span>
          </template>
          <validation-provider
            #default="{ errors }"
            name="Mobile"
            rules="required|integer"
          >
            <b-form-input
              id="mobile_number"
              v-model="mobile_number"
              :state="errors.length > 0 ? false : null"
              placeholder="Mobile"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group label-for="email">
          <template #label>
            Email <span class="text-danger">*</span>
          </template>
          <validation-provider
            #default="{ errors }"
            name="Email"
            rules="required|email"
          >
            <b-form-input
              id="email"
              v-model="email"
              :state="errors.length > 0 ? false : null"
              placeholder="Email"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <VueSelectPaginated
          placeholder="Role"
          disabled
          name="Role"
          label="name"
          rules="required"
          searchBy="name"
          :prevSelected="role"
          :getListMethod="getRoles"
          @setMethod="
            (value) => {
              role = value;
            }
          "
        />
        <b-form-group label-for="profile_status">
          <template #label>
            Profile Status <span class="text-danger">*</span>
          </template>
            <b-form-select v-model="profile_status" :options="status_options"></b-form-select>
        </b-form-group>
      </b-form>
    </validation-observer>
    <template #modal-footer>
      <b-form-group class="text-right">
        <b-button type="submit" variant="info" pill @click="validationForm">
          Update
        </b-button>
      </b-form-group>
    </template>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import VueSelectPaginated from "@/components/ui/VueSelectPaginated.vue";
import { required } from "@validations";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import util from "@/util.js";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";


export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    VueSelectPaginated,
  },
  props: {
    user: Object,
  },
  mixins: [util, togglePasswordVisibility],
  data() {
    return {
      username: "",
      password: "",
      name: "",
      mobile_number: "",
      email: "",
      role: null,
      status_options: [
        { value: 'Approved', text: 'Approved' },
        { value: 'Rejected', text: 'Rejected' },
        { value: 'Pending', text: 'Pending' },
        { value: 'Left', text: 'Left' },
      ],
      profile_status: "",
    };
  },
  async mounted() {
    if (this.user) {
      this.name = this.user.name;
      this.username = this.user.username;
      this.mobile_number = this.user.mobile_number;
      this.email = this.user.email;
      this.role = this.user.role_data;
      this.profile_status = this.user.profile_status;
    }
  },
  methods: {
    ...mapActions({
      updateUser: "appData/updateUser",
      getRoles: "appData/getRoles",
    }),
    async validationForm() {
      const success = await this.$refs.editUserFormValidation.validate();
      if (success) {
        await this.submit();
      }
    },
    async submit() {
      try {
        const formData = new FormData();
        if (this.password){
          formData.append("password", this.password);
        }
        formData.append("name", this.name);
        formData.append("mobile_number", this.mobile_number);
        formData.append("email", this.email);
        formData.append("role", this.role.id);
        formData.append("profile_status", this.profile_status);
        formData.append("updated_by", this.getLoggedInUser.id);
        const res = await this.updateUser({
          payload: formData,
          pk: this.user.id,
        });
        if (res.status === 200) {
          this.$toast(
            {
              component: ToastificationContent,
              props: {
                title: "User Updated Successfully",
                variant: "success",
                icon: "BellIcon",
              },
            },
            {
              position: "top-right",
            }
          );
          this.$nextTick(() => {
            this.$bvModal.hide("edit-user-modal");
          });
          this.$emit("modalClosed");
        }
      } catch (error) {
        this.displayError(error);
      }
    },
  },
  computed: {
    ...mapGetters({ getLoggedInUser: "appData/getLoggedInUser" }),
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
  },
};
</script>

<style></style>
